import {AppConfig, BaseAppProps, BaseAppRefs, BaseAppState, clearProvisioningId} from "../shared/BaseApp";
import React, {createRef, ReactElement, RefObject, useState} from "react";
import {TabInfo} from "../shared/TabsContainer";
import {ProvisionedApp} from "../shared/ProvisionedApp";
import {AppPrefListener, ProvisionedAppConfig} from "../shared/types";
import {LoginConfig} from "../shared/Login";
import {StyledBoxColumn} from "../shared/StyledComponents";
import {PAGE_FRAGMENT_HALF_WIDTH, PD_XLG2} from "../shared/dimens";
import {Button, Card, createTheme, Typography} from "@mui/material";
import {THEME_COLOR_PRIMARY_DARK, THEME_OPTIONS, THEME_OPTIONS_DARK} from "./theme";
import {DirectoryContextTrait, DirectoryUser} from "../shared/directory/types";
import {User} from "@firebase/auth";
import {FormGenContainer, FormGenLayoutType} from "../shared/FormGenContainer";
import {FyneAIFormGenRendererFactory} from "./FyneAIFormGenRendererFactory";
import {BaseFyneAIAppPrefs} from "./BaseFyneAIAppPrefs";
import {PathProps} from "../shared";
import {PasskeyConfig} from "@/shared/passkey";
import {PasskeyInput} from "../shared/PasskeyInput";
import {FIREBASE_CONFIG} from "./consts";
import {
  NotificationsContextTrait,
  NotificationsSource,
  Resource,
  ResourceAccessRole,
  ResourceContextTrait,
  ResourceData,
  ResourceNodes,
  ResourceType
} from "./types";
import {AppPrefs} from "../app/app_prefs";
import {BaseAppPrefs} from "../shared/BaseAppPrefs";
import {FyneAIInit} from "./init";
import {ResourceContext, ResourceContextProps} from "./contexts";
import {AppNotificationsSource} from "./notifications/AppNotificationsSource";

const SHARED_TAB_ITEMS: TabInfo<string>[] = [];

function UnlockFragment(props: { path: PathProps, onUnlockWithPin(pin: string): void }): ReactElement {
  const config: PasskeyConfig = {length: 4, type: "numeric"};
  const [passkey, setPasskey] = useState<string>("");
  return <Card>
    <StyledBoxColumn style={{width: PAGE_FRAGMENT_HALF_WIDTH, padding: PD_XLG2}}>
      <Typography style={{textAlign: "center"}}>Enter PIN to unlock</Typography>
      <StyledBoxColumn style={{alignItems: "center"}}>
        <PasskeyInput
          config={config}
          onPasskeyInputChanged={passkey => setPasskey(passkey)}
        />
      </StyledBoxColumn>
      <Button
        onClick={() => props.onUnlockWithPin(passkey)}
        variant={"contained"} disabled={passkey.length !== config.length}>Unlock</Button>
    </StyledBoxColumn>
  </Card>;
}

export class FyneAIAppRefs {

  constructor(
    readonly searchText: RefObject<HTMLInputElement>) {
  }
}

export abstract class FyneAIApp<P extends BaseAppProps = BaseAppProps, S extends BaseAppState = BaseAppState> extends ProvisionedApp<P, S>
  implements DirectoryContextTrait,
    ResourceContextTrait,
    NotificationsContextTrait,
    AppPrefListener {

  private static appRefs: FyneAIAppRefs;

  static getAppRefs(): FyneAIAppRefs {
    if (!FyneAIApp.appRefs) {
      FyneAIApp.appRefs = FyneAIApp.createAppRefs();
    }
    return FyneAIApp.appRefs;
  }

  getAppPrefs<T extends BaseAppPrefs>(): T {
    // @ts-ignore
    return AppPrefs.getInstance() as T;
  }

  private static createAppRefs() {
    return new FyneAIAppRefs(
      createRef<HTMLInputElement>());
  }

  getAppRefs<T extends BaseAppRefs>(): T {
    // @ts-ignore
    return App.getAppRefs() as T;
  }

  protected getResourceAccessRole(): ResourceAccessRole | undefined {
    return undefined;
  }

  private resourceContext: ResourceContextProps;

  protected async onAppInitWithLoggedInUser(user: User): Promise<void> {
    await super.onAppInitWithLoggedInUser(user);
    const accessRole = this.getResourceAccessRole();
    await FyneAIInit(user, accessRole);

    const resNodes = ResourceNodes.getInstance().getListItems();
    this.organizationResource = resNodes
      .find(item => item.type === ResourceType.ORGANIZATION).organization;
    const appPrefs = AppPrefs.getInstance();
    const selectedResourceId = appPrefs.getSelectedResourceId();
    this.resourceContext = {
      accessRole: accessRole,
      resources: resNodes.map(node => node.getResource()),
      selectedResource: resNodes.find(node => node.id === selectedResourceId)?.getResource(),
      setSelectedResource: resource => {
        appPrefs.setSelectedResourceId(resource.getResourceNode().id);
        window.location.href = "/";
      },
    }

    // FormGen
    FormGenContainer.registerFactory(FormGenLayoutType.DEFAULT, new FyneAIFormGenRendererFactory());

    this.notificationsSource = new AppNotificationsSource(ResourceData.shared().notifications, "/notifications");
    await this.notificationsSource.init();
  }

  private myDirectoryUser: DirectoryUser;

  getMyDirectoryUser(): DirectoryUser | undefined {
    return this.myDirectoryUser;
  }

  private organizationResource: Resource;

  getOrganizationResource(): Resource {
    return this.organizationResource;
  }

  private notificationsSource;

  getNotificationsSource(): NotificationsSource {
    return this.notificationsSource;
  }

  componentDidMount() {
    super.componentDidMount();
    const prefs = this.getAppPrefs<BaseFyneAIAppPrefs>();
    prefs.addListener(BaseFyneAIAppPrefs.PREF_LOCKED, this);
  }

  componentWillUnmount() {
    const prefs = this.getAppPrefs<BaseFyneAIAppPrefs>();
    prefs.removeListener(BaseFyneAIAppPrefs.PREF_LOCKED, this);
    super.componentWillUnmount();
  }

  onPrefChanged(key: string) {
    super.onPrefChanged(key);
    switch (key) {
      case BaseFyneAIAppPrefs.PREF_LOCKED:
        this.forceUpdate();
        break;
    }
  }

  getProvisionedAppId(): string {
    return "";
  }

  getProvisionedAppConfig(): ProvisionedAppConfig {
    return undefined;
  }

  protected abstract getAppName(): string;

  protected getThemeColor(): string {
    return THEME_COLOR_PRIMARY_DARK;
  }

  protected getSignupEnabled(): boolean {
    return false;
  }

  protected getLoginConfig(): LoginConfig | null | undefined {
    return {
      provisioning: {
        enabled: true,
        signupDisabled: !this.getSignupEnabled(),
        onChangeProvisioning: () => {
          clearProvisioningId();
        },
      },
    };
  }

  onCreateAppConfig(): AppConfig {
    const themeLight = createTheme(THEME_OPTIONS);
    const themeDark = createTheme(THEME_OPTIONS_DARK);
    return {
      name: this.getAppName(),
      icon: "/logo192.png",
      logo: "/logo.gif",
      stamp: "/images/fyneai_logotype.svg",
      themeColor: this.getThemeColor(),
      defaultUserImage: "/images/default_user.png",
      theme: this.getPreferredColorScheme() === "dark" ? themeDark : themeLight,
      themeLight: themeLight,
      themeDark: themeDark,
      privacyUrl: "",
      termsUrl: "",
      loginConfig: this.getLoginConfig(),
      styleConfig: {
        defaultContainerSize: "xxlg",
      },
      firebaseConfig: {options: FIREBASE_CONFIG},
    };
  }

  protected renderMain(): ReactElement {
    const prefs = this.getAppPrefs<BaseFyneAIAppPrefs>();
    if (prefs.isLocked()) {
      return <StyledBoxColumn
        style={{
          width: "100vw",
          height: "100vh",
          zIndex: 10000,
          background: this.getAppConfig().theme.palette.background.default,
          // background: 'url("/assets/wallpapers/1.jpg") no-repeat center center fixed',
          // backgroundSize: "cover",
          alignItems: "center",
          justifyContent: "center"
        }}>
        <UnlockFragment
          path={this.props.path}
          onUnlockWithPin={pin => {
            prefs.setLocked(false);
          }}/>
      </StyledBoxColumn>;
    }
    return <ResourceContext.Provider value={this.resourceContext}>
      {this.renderFyneAIMain()}
    </ResourceContext.Provider>;
  }

  protected abstract renderFyneAIMain(): ReactElement;
}